import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  FileText,
  Brain,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Search,
  Download,
  ArrowUp,
  Users,
  Package,
  Cpu,
  HardDrive,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { apiClient, ValidateFixesResponse, AttackChain } from '@/api/client';
import { AttackChainVisualization } from '@/components/AttackChainVisualization';
import { 
  webSocketClient, 
  RiskAssessmentAgentUpdate,
  WebSocketMessage 
} from '@/api/websocket';
import { AffectedResource } from '@/api/client';
import { ModeToggle } from '@/components/mode-toggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import '@xyflow/react/dist/style.css';
import { MobileMenuButton } from '@/components/MobileMenuButton';

// Professional markdown renderer component using react-markdown
const MarkdownRenderer: React.FC<{ content: string; className?: string }> = ({ content, className = "" }) => {
  return (
    <div className={`prose prose-sm dark:prose-invert max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom styling for different elements
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold text-foreground mb-4 mt-6">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-semibold text-foreground mb-4 mt-6">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-semibold text-foreground mb-3 mt-4">{children}</h3>
          ),
          p: ({ children }) => (
            <p className="mb-4 text-foreground leading-relaxed">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-2 my-4 ml-4">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-2 my-4 ml-4">{children}</ol>
          ),
          li: ({ children }) => {
            // Convert children to array for easier manipulation
            const content = React.Children.toArray(children);
            
            // Check if the first child is a paragraph or text node that contains a title (ends with colon)
            const firstChild = content[0];
            let titleText = '';
            let hasTitle = false;
            
            // Check if first child is a paragraph with title
            if (React.isValidElement(firstChild) && firstChild.type === 'p') {
              const pProps = firstChild.props as { children?: React.ReactNode };
              const pContent = React.Children.toArray(pProps.children || []);
              const firstPText = pContent[0];
              if (typeof firstPText === 'string' && firstPText.trim().endsWith(':')) {
                titleText = firstPText.trim();
                hasTitle = true;
              }
            }
            // Check if first child is direct text with title
            else if (typeof firstChild === 'string' && firstChild.trim().endsWith(':')) {
              titleText = firstChild.trim();
              hasTitle = true;
            }
            
            if (hasTitle) {
              // Render title inline with bullet point, and remaining content indented
              const remainingContent = content.slice(1);
              
              return React.createElement(
                'li',
                { className: 'mb-2 text-foreground' },
                React.createElement('span', { className: 'font-semibold' }, titleText),
                remainingContent.length > 0 && React.createElement(
                  'div',
                  { className: 'ml-4 mt-1 text-foreground leading-relaxed' },
                  ...remainingContent
                )
              );
            }
            
            // Regular list item without title
            return React.createElement('li', { className: 'mb-1 text-foreground' }, children);
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground">{children}</blockquote>
          ),
          code: ({ children, className, ...props }: any) => {
            const isInline = !className?.includes('language-');
            if (isInline) {
              return (
                <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <pre className="bg-muted p-3 rounded-md border font-mono text-sm overflow-x-auto my-3">
                <code className="text-foreground" {...props}>{children}</code>
              </pre>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-muted p-3 rounded-md border font-mono text-sm overflow-x-auto my-3">
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border-collapse border border-border">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-muted">{children}</thead>
          ),
          tbody: ({ children }) => (
            <tbody>{children}</tbody>
          ),
          tr: ({ children }) => (
            <tr className="border-b border-border">{children}</tr>
          ),
          th: ({ children }) => (
            <th className="border border-border px-4 py-2 text-left font-semibold text-foreground">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-border px-4 py-2 text-foreground">
              {children}
            </td>
          ),
          hr: () => (
            <hr className="my-6 border-t border-border" />
          ),
          a: ({ href, children }) => (
            <a 
              href={href} 
              className="text-primary hover:underline" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic">{children}</em>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

// Define agent types for consistent ordering and mapping
const AGENT_TYPES = [
  'orchestrator', 
  'network_policy', 
  'privilege_escalation', 
  'rbac',
  'container_image_security',
  'unrestricted_resource_usage',
  'storage_security',
];

const agentIcons: { [key: string]: React.ComponentType<any> } = {
  'orchestrator': Brain,
  'network_policy': Shield,
  'privilege_escalation': ArrowUp,
  'rbac': Users,
  'container_image_security': Package,
  'unrestricted_resource_usage': Cpu,
  'storage_security': HardDrive,
};

const getAgentDisplayName = (type: string) => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + ' Agent';
};


interface RiskAssessment {
  id: string;
  cluster_id: string;
  cluster_name: string;
  status: 'ongoing' | 'completed' | 'failed';
  severity_score: number;
  created_at: string;
  completed_at?: string;
  findings_count?: number;
  high_risk_findings?: number;
  medium_risk_findings?: number;
  low_risk_findings?: number;
  final_report?: string;
  error_message?: string;
  structured_findings?: RiskFinding[];
  agent_thinking?: ApiAgentThinking[]; // From REST API
  validation_status?: 'pending' | 'running' | 'completed' | 'failed';
  validation_started_at?: string;
  validation_completed_at?: string;
  validation_results?: ValidateFixesResponse;
}

interface ApiAgentThinking {
  agent_type: string;
  thinking_overview: string;
  turn_number: number;
  tool_calls: { tool: string; cmd: string; output: string; success: boolean; error: string }[];
}

interface RiskFinding {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low' | 'critical';
  category: string;
  recommendation: string;
  affected_resources: AffectedResource[];
}

// State for a single thinking update
interface ThinkingUpdate {
  thinking_overview: string;
  turn_number: number;
  status: string;
  findings?: string;
  tool_calls: any[];
  timestamp: Date;
}

// State for each agent's collected thoughts
interface AgentThinkingState {
  name: string;
  icon: React.ComponentType<any>;
  updates: ThinkingUpdate[];
}

const AGENT_TYPES_FOR_GRID = AGENT_TYPES.filter(type => type !== 'orchestrator');

const KubernetesRiskAssessmentDetail: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<RiskAssessment | null>(null);
  const [agentThinking, setAgentThinking] = useState<Map<string, AgentThinkingState>>(() => {
    const initialMap = new Map<string, AgentThinkingState>();
    AGENT_TYPES.forEach(type => {
      initialMap.set(type, {
        name: getAgentDisplayName(type),
        icon: agentIcons[type] || Shield,
        updates: [],
      });
    });
    return initialMap;
  });
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(() => new Set(AGENT_TYPES_FOR_GRID));
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('report');
  const [selectedAgentForModal, setSelectedAgentForModal] = useState<string | null>(null);
  const [attackChains, setAttackChains] = useState<AttackChain[]>([]);
  const [isLoadingAttackChains, setIsLoadingAttackChains] = useState(false);
  const [hasLoadedAttackChains, setHasLoadedAttackChains] = useState(false);
  const [isGeneratingAttackChains, setIsGeneratingAttackChains] = useState(false);

  // Pagination state for issues
  const [currentPage, setCurrentPage] = useState(1);
  const [isValidatingFixes, setIsValidatingFixes] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidateFixesResponse | null>(null);
  const validationPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const itemsPerPage = 5;
  
  // Pagination state for validation results
  const [validationCurrentPage, setValidationCurrentPage] = useState(1);
  const validationItemsPerPage = 5;

  // State for dismissing findings
  const [dismissingFindingId, setDismissingFindingId] = useState<string | null>(null);

  const handleDownloadReport = () => {
    if (!assessment?.final_report) {
      console.error("No report content to download.");
      return;
    }

    const blob = new Blob([assessment.final_report], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `risk-assessment-report-${assessment.id}.md`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const toggleAgentExpansion = useCallback((agentType: string) => {
    setExpandedAgents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentType)) {
        newSet.delete(agentType);
      } else {
        newSet.add(agentType);
      }
      return newSet;
    });
  }, []);

  const loadAssessmentDetailsCallback = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const assessmentDetails = await apiClient.getRiskAssessmentDetails(id);
      
      const assessmentData: RiskAssessment = {
        id: assessmentDetails.assessment_id,
        cluster_id: assessmentDetails.cluster_id,
        cluster_name: assessmentDetails.cluster_name,
        status: assessmentDetails.status as any,
        severity_score: assessmentDetails.severity_score || 0,
        created_at: assessmentDetails.created_at,
        completed_at: assessmentDetails.completed_at,
        findings_count: assessmentDetails.findings_count,
        high_risk_findings: assessmentDetails.high_risk_findings,
        medium_risk_findings: assessmentDetails.medium_risk_findings,
        low_risk_findings: assessmentDetails.low_risk_findings,
        final_report: assessmentDetails.final_report,
        error_message: assessmentDetails.error_message,
        structured_findings: assessmentDetails.structured_findings || [],
        agent_thinking: assessmentDetails.agent_thinking || [],
        validation_status: assessmentDetails.validation_status,
        validation_started_at: assessmentDetails.validation_started_at,
        validation_completed_at: assessmentDetails.validation_completed_at,
        validation_results: assessmentDetails.validation_results,
      };
      
      setAssessment(assessmentData);

      // Check validation state and handle accordingly
      if (assessmentDetails.validation_status === 'completed' && assessmentDetails.validation_results) {
        setValidationResults(assessmentDetails.validation_results);
        setValidationCurrentPage(1); // Reset pagination when loading existing results
        setIsValidatingFixes(false);
      } else if (assessmentDetails.validation_status === 'running') {
        setIsValidatingFixes(true);
        // Polling will be started by the separate useEffect when assessment state is set
      } else {
        setIsValidatingFixes(false);
      }

      // Process historical agent thinking data
      if (assessmentData.agent_thinking) {
        setAgentThinking(prevMap => {
          const newMap = new Map(prevMap);

          // Group updates by agent type first
          const updatesByAgent = new Map<string, ThinkingUpdate[]>();

          assessmentData.agent_thinking?.forEach(thinking => {
            const agentType = thinking.agent_type || 'unknown';
            const newUpdate: ThinkingUpdate = {
              thinking_overview: thinking.thinking_overview,
              turn_number: thinking.turn_number,
              status: 'completed', // Historical data is considered completed
              tool_calls: thinking.tool_calls || [],
              timestamp: new Date(assessmentData.created_at), // Use assessment creation as fallback time
            };

            if (!updatesByAgent.has(agentType)) {
              updatesByAgent.set(agentType, []);
            }
            updatesByAgent.get(agentType)!.push(newUpdate);
          });

          // Now update each agent with NEW arrays (no mutation)
          updatesByAgent.forEach((updates, agentType) => {
            const agentState = newMap.get(agentType);
            if (agentState) {
              // Filter out duplicates and create a NEW sorted array
              const existingTurnNumbers = new Set(agentState.updates.map(u => u.turn_number));
              const newUpdates = updates.filter(u => !existingTurnNumbers.has(u.turn_number));
              const combinedUpdates = [...agentState.updates, ...newUpdates]
                .sort((a, b) => a.turn_number - b.turn_number);
              newMap.set(agentType, { ...agentState, updates: combinedUpdates });
            }
          });

          return newMap;
        });
      }
    } catch (error) {
      console.error('Failed to load assessment details:', error);
      setAssessment(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAttackChains = useCallback(async (id: string) => {
    if (!id) return;

    setIsLoadingAttackChains(true);
    try {
      const response = await apiClient.getAttackChainsByAssessment(id);
      setAttackChains(response.attack_chains || []);
    } catch (error) {
      console.error('Failed to load attack chains:', error);
      setAttackChains([]);
    } finally {
      setIsLoadingAttackChains(false);
      setHasLoadedAttackChains(true);
    }
  }, []);

  const generateAttackChains = useCallback(async () => {
    if (!assessmentId) return;

    setIsGeneratingAttackChains(true);
    try {
      await apiClient.generateAttackChains(assessmentId);
      
      // Poll for completion - attack chain generation happens async
      let attempts = 0;
      const maxAttempts = 12; // 12 attempts x 5 seconds = 60 seconds max wait
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const response = await apiClient.getAttackChainsByAssessment(assessmentId);
          
          // Check if we have attack chains (new or existing)
          if (response.attack_chains && response.attack_chains.length > 0) {
            setAttackChains(response.attack_chains);
            clearInterval(pollInterval);
            setIsGeneratingAttackChains(false);
          } else if (attempts >= maxAttempts) {
            // Timeout - stop polling and show what we have
            clearInterval(pollInterval);
            setIsGeneratingAttackChains(false);
            await loadAttackChains(assessmentId);
          }
        } catch (err) {
          console.error('Error polling for attack chains:', err);
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setIsGeneratingAttackChains(false);
          }
        }
      }, 5000); // Poll every 5 seconds
    } catch (error: any) {
      console.error('Failed to generate attack chains:', error);
      setIsGeneratingAttackChains(false);
    }
  }, [assessmentId, loadAttackChains]);

  const stopValidationPolling = useCallback(() => {
    if (validationPollingIntervalRef.current) {
      clearInterval(validationPollingIntervalRef.current);
      validationPollingIntervalRef.current = null;
    }
  }, []);

  const startValidationPolling = useCallback(() => {
    // Clear any existing polling interval first
    stopValidationPolling();

    const interval = setInterval(async () => {
      // Check if polling was stopped externally
      if (validationPollingIntervalRef.current !== interval) {
        return;
      }

      if (!assessmentId) {
        clearInterval(interval);
        validationPollingIntervalRef.current = null;
        return;
      }

      try {
        // Poll for validation status without affecting main loading state
        const assessmentDetails = await apiClient.getRiskAssessmentDetails(assessmentId);
        
        
        if (assessmentDetails.validation_status === 'completed' && assessmentDetails.validation_results) {
          setValidationResults(assessmentDetails.validation_results);
          setValidationCurrentPage(1); // Reset pagination when polling completes
          setIsValidatingFixes(false);
          clearInterval(interval);
          validationPollingIntervalRef.current = null;
          
          // Update only validation-related fields in the assessment state
          setAssessment(prev => prev ? {
            ...prev,
            validation_status: assessmentDetails.validation_status,
            validation_completed_at: assessmentDetails.validation_completed_at,
            validation_results: assessmentDetails.validation_results
          } : null);
        } else if (assessmentDetails.validation_status === 'failed') {
          setIsValidatingFixes(false);
          clearInterval(interval);
          validationPollingIntervalRef.current = null;
          
          // Update only validation status without affecting other state
          setAssessment(prev => prev ? {
            ...prev,
            validation_status: 'failed'
          } : null);
        } else if (assessmentDetails.validation_status === 'running' || assessmentDetails.validation_status === 'pending') {
          // Continue polling - don't update any state to avoid re-renders
        } else {
          setIsValidatingFixes(false);
          clearInterval(interval);
          validationPollingIntervalRef.current = null;
        }
      } catch (err) {
        console.error('Error polling validation status:', err);
        setIsValidatingFixes(false);
        clearInterval(interval);
        validationPollingIntervalRef.current = null;
      }
    }, 10000); // Poll every 10 seconds

    validationPollingIntervalRef.current = interval;
  }, [assessmentId, stopValidationPolling]);

  useEffect(() => {
    if (assessmentId) {
      loadAssessmentDetailsCallback(assessmentId);
    }
  }, [assessmentId, loadAssessmentDetailsCallback]);

  // Separate useEffect for cleanup on unmount
  useEffect(() => {
    return () => {
      stopValidationPolling();
    };
  }, [stopValidationPolling]);

  // WebSocket connection and message handling
  useEffect(() => {
    if (!assessmentId) return;

    let messageHandler: ((message: WebSocketMessage) => void) | null = null;

    const connectWebSocket = async () => {
      try {
        if (!webSocketClient.isConnected()) {
          await webSocketClient.connect();
        }

        messageHandler = (message: WebSocketMessage) => {
          if (message.type === 'risk_assessment_agent_thinking' && message.content.assessment_id === assessmentId) {
            const update = message.content as RiskAssessmentAgentUpdate;
            
            const newThinkingUpdate: ThinkingUpdate = {
              thinking_overview: update.thinking_overview,
              turn_number: update.turn_number,
              status: update.status,
              findings: update.findings,
              tool_calls: (update as any).tool_calls || [],
              timestamp: new Date(),
            };

            setAgentThinking(prevMap => {
              const newMap = new Map(prevMap);
              const agentState = newMap.get(update.agent_type);
              if (agentState) {
                // Prevent adding duplicate turn numbers
                if (!agentState.updates.some(u => u.turn_number === newThinkingUpdate.turn_number)) {
                    // Create a NEW updates array to ensure React detects the change
                    const newUpdates = [...agentState.updates, newThinkingUpdate]
                      .sort((a, b) => a.turn_number - b.turn_number);
                    newMap.set(update.agent_type, { ...agentState, updates: newUpdates });
                }
              }
              return newMap;
            });

            // Auto-expand the agent dropdown when receiving updates
            setExpandedAgents(prev => new Set(prev).add(update.agent_type));
          }
        };

        webSocketClient.onMessage(messageHandler);
      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
      }
    };

    connectWebSocket();

    return () => {
      if (messageHandler) {
        webSocketClient.offMessage(messageHandler);
      }
    };
  }, [assessmentId]);

  // Start polling when validation status becomes 'running' (e.g., on page load with running validation)
  useEffect(() => {
    if (assessment?.validation_status === 'running' && !validationPollingIntervalRef.current) {
      startValidationPolling();
    }
  }, [assessment?.validation_status, startValidationPolling]);

  // Load attack chains when the attack-chains tab is activated
  useEffect(() => {
    if (activeTab === 'attack-chains' && assessmentId && !hasLoadedAttackChains && !isLoadingAttackChains) {
      loadAttackChains(assessmentId);
    }
  }, [activeTab, assessmentId, hasLoadedAttackChains, isLoadingAttackChains, loadAttackChains]);

  const validateFixes = useCallback(async () => {
    if (!assessmentId) return;

    // Clear previous results and start fresh validation
    setIsValidatingFixes(true);
    setValidationResults(null);

    // Clear any existing polling interval
    stopValidationPolling();

    try {
      const results = await apiClient.validateRiskAssessmentFixes(assessmentId);

      if (results.validation_status === 'in_progress') {
        // Start polling for updates only when user explicitly triggers validation
        startValidationPolling();
      } else if (results.validation_status === 'completed') {
        setValidationResults(results);
        setValidationCurrentPage(1); // Reset pagination when new results arrive
        setIsValidatingFixes(false);
      } else {
        setIsValidatingFixes(false);
      }
    } catch (err: any) {
      console.error('Failed to validate fixes:', err);
      setIsValidatingFixes(false);
    }
  }, [assessmentId, startValidationPolling, stopValidationPolling]);

  const handleDismissFinding = useCallback(async (findingId: string) => {
    if (!assessmentId) return;

    setDismissingFindingId(findingId);
    try {
      await apiClient.dismissFinding(assessmentId, findingId);
      // Reload assessment details to update counts and remove dismissed finding
      await loadAssessmentDetailsCallback(assessmentId);
      
      // If attack chains were loaded, they may now be stale - reload them to trigger staleness check
      if (hasLoadedAttackChains) {
        await loadAttackChains(assessmentId);
      }
    } catch (err: any) {
      console.error('Failed to dismiss finding:', err);
    } finally {
      setDismissingFindingId(null);
    }
  }, [assessmentId, loadAssessmentDetailsCallback, hasLoadedAttackChains, loadAttackChains]);

  const getSeverityColor = (score: number) => {
    if (score >= 8) return 'bg-red-500';
    if (score >= 6) return 'bg-orange-500';
    if (score >= 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getSeverityBadgeColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'medium': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'low': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'; // Treat critical as high
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'ongoing': return <Clock className="h-4 w-4 text-blue-500" />;
      default: return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Assessment Not Found</h2>
          <p className="text-gray-600 mb-4">The requested assessment could not be found.</p>
          <Button onClick={() => navigate('/risk-assessment')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assessments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <MobileMenuButton />
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">Risk Assessment Details</h1>
            <div className="flex items-center space-x-2">
              {getStatusIcon(assessment.status)}
              <Badge variant="outline" className="capitalize">
                {assessment.status}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Activity className="h-4 w-4" />
              <span>{assessment.cluster_name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>Created {new Date(assessment.created_at).toLocaleDateString('en-US')}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center flex-shrink-0">
          <ModeToggle />
        </div>
      </div>

      {/* Tabs for Report, Attack Path Analysis, Chain of Thought, and Fix Validation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="report" className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>Risk Assessment Report</span>
          </TabsTrigger>
          <TabsTrigger value="attack-chains" className="flex items-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>Attack Path Analysis</span>
          </TabsTrigger>
          <TabsTrigger value="thought" className="flex items-center space-x-2">
            <Brain className="h-4 w-4" />
            <span>Chain of Thought</span>
          </TabsTrigger>
          <TabsTrigger value="validation" className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4" />
            <span>Fix Validation</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="report" className="mt-6">
          <div className="space-y-6">
            {/* Assessment Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <span>Risk Assessment Summary</span>
                  </span>
                  <div className="flex items-center gap-2">
                    {assessment?.final_report && (
                      <Button variant="outline" size="sm" onClick={handleDownloadReport}>
                        <Download className="h-4 w-4 mr-2" />
                        Download Report
                      </Button>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={validateFixes}
                          disabled={isValidatingFixes || !assessment?.structured_findings?.length}
                        >
                          {isValidatingFixes ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          {isValidatingFixes ? 'Validating...' : validationResults ? 'Re-validate Fixes' : 'Validate Fixes'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{validationResults ? 'Run fresh validation to check if additional vulnerabilities have been fixed since the last check' : 'Run comprehensive tests to check if the vulnerabilities found in the risk assessment were fixed'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600">Risk Score</p>
                    <div className="flex items-center space-x-2">
                      <div className={cn("w-3 h-3 rounded-full", getSeverityColor(assessment.severity_score))} />
                      <span className="text-2xl font-bold">{assessment.severity_score.toFixed(1)}/10</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600">Total Findings</p>
                    <p className="text-2xl font-bold">{assessment.findings_count || 0}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600">Created</p>
                    <p className="text-sm">{new Date(assessment.created_at).toLocaleString('en-US')}</p>
                  </div>
                  {assessment.completed_at && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-600">Completed</p>
                      <p className="text-sm">{new Date(assessment.completed_at).toLocaleString('en-US')}</p>
                    </div>
                  )}
                </div>

                {/* Findings Breakdown */}
                <Separator className="my-4" />
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{assessment.high_risk_findings || 0}</div>
                    <div className="text-sm text-gray-600">High Risk</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{assessment.medium_risk_findings || 0}</div>
                    <div className="text-sm text-gray-600">Medium Risk</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{assessment.low_risk_findings || 0}</div>
                    <div className="text-sm text-gray-600">Low Risk</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Risk Assessment Summary */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-px h-8 bg-gradient-to-b from-blue-500 to-cyan-500"></div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Risk Assessment Report</h3>
              </div>
              
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between hover:bg-muted/50">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      View Full Risk Assessment Report
                    </span>
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="overflow-y-auto max-h-96 bg-gradient-to-br from-neutral-50/90 to-neutral-100/90 dark:from-neutral-900/90 dark:to-neutral-800/90 border border-neutral-200/50 dark:border-neutral-700/50 rounded-xl p-4 backdrop-blur-sm shadow-inner">
                        <MarkdownRenderer 
                          content={assessment.final_report || 'No assessment report available'} 
                          className="text-sm"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Vertical Line with Issues Found Header */}
            {assessment && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-px h-8 bg-gradient-to-b from-blue-500 to-cyan-500"></div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Issues Found</h3>
                    <Badge variant="outline" className="ml-2">
                      {assessment.findings_count || 0} total
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, assessment.findings_count || 0)} of {assessment.findings_count || 0}
                  </div>
                </div>

                {/* Issues List */}
                <div className="space-y-4">
                  {assessment.structured_findings?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((finding) => (
                    <Card 
                      key={finding.id} 
                      className={cn(
                        "border-l-4 transition-all hover:shadow-md cursor-pointer",
                        finding.severity === 'high' || finding.severity === 'critical' ? 'border-l-red-500' :
                        finding.severity === 'medium' ? 'border-l-orange-500' :
                        'border-l-yellow-500'
                      )}
                      onClick={() => navigate(`/risk-assessment/${assessmentId}/findings/${finding.id}`)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{finding.title}</CardTitle>
                          <div className="flex items-center space-x-2">
                            <div className={cn(
                              "w-3 h-3 rounded-full",
                              finding.severity === 'high' || finding.severity === 'critical' ? 'bg-red-500' :
                              finding.severity === 'medium' ? 'bg-orange-500' :
                              'bg-yellow-500'
                            )} />
                            <Badge className={cn("pointer-events-none", getSeverityBadgeColor(finding.severity))}>
                              {finding.severity === "critical" ? "high" : finding.severity}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 hover:bg-destructive/10 hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDismissFinding(finding.id);
                              }}
                              disabled={dismissingFindingId === finding.id}
                            >
                              {dismissingFindingId === finding.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{finding.category}</p>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm mb-3 text-gray-700 dark:text-gray-300">{finding.description}</p>
                        <div className="mb-3">
                          <p className="text-sm font-medium mb-1">Recommendation:</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{finding.recommendation}</p>
                        </div>
                        <div className="mb-3">
                          <p className="text-sm font-medium mb-1">Affected Resources:</p>
                          <div className="flex flex-wrap gap-1">
                            {(finding.affected_resources || []).length > 0 ? (
                              (finding.affected_resources || []).map((resource, index) => (
                                <Tooltip key={index}>
                                  <TooltipTrigger asChild>
                                    <Badge 
                                      variant="secondary" 
                                      className="text-xs cursor-help hover:bg-secondary/80 transition-colors"
                                    >
                                      {resource.namespace ? `${resource.namespace}/${resource.name}` : resource.name}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{resource.resource_type ? `${resource.resource_type}${resource.namespace ? ` in ${resource.namespace}` : ' (cluster-scoped)'}` : 'Unknown resource type'}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ))
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                Cluster wide
                              </Badge>
                            )}
                          </div>
                        </div>
                        {/* Click to view remediation indicator */}
                        <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground border-t">
                          <FileText className="h-3 w-3" />
                          <span>Click to view YAML remediation steps</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Pagination Controls */}
                {(assessment.findings_count || 0) > itemsPerPage && (
                  <div className="flex items-center justify-between pt-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Page {currentPage} of {Math.ceil((assessment.findings_count || 0) / itemsPerPage)}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.ceil((assessment.findings_count || 0) / itemsPerPage) }, (_, i) => i + 1)
                          .filter(page => {
                            const totalPages = Math.ceil((assessment.findings_count || 0) / itemsPerPage);
                            if (totalPages <= 7) return true;
                            if (page === 1 || page === totalPages) return true;
                            if (Math.abs(page - currentPage) <= 1) return true;
                            return false;
                          })
                          .map((page, index, array) => {
                            const prevPage = array[index - 1];
                            const showEllipsis = prevPage && page - prevPage > 1;
                            return (
                              <React.Fragment key={page}>
                                {showEllipsis && (
                                  <span className="text-gray-400 px-2">...</span>
                                )}
                                <Button
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setCurrentPage(page)}
                                  className="w-8 h-8 p-0"
                                >
                                  {page}
                                </Button>
                              </React.Fragment>
                            );
                          })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(Math.ceil((assessment.findings_count || 0) / itemsPerPage), currentPage + 1))}
                        disabled={currentPage === Math.ceil((assessment.findings_count || 0) / itemsPerPage)}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        {/* Fix Validation Tab */}
        <TabsContent value="validation" className="mt-6">
          <div className="space-y-6">
            {/* Validation Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Fix Validation
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={validateFixes}
                        disabled={isValidatingFixes || !assessment?.structured_findings?.length}
                      >
                        {isValidatingFixes ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4 mr-2" />
                        )}
                        {isValidatingFixes ? 'Validating...' : validationResults ? 'Re-validate Fixes' : 'Validate Fixes'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{validationResults ? 'Run fresh validation to check if additional vulnerabilities have been fixed since the last check' : 'Run comprehensive tests to check if the vulnerabilities found in the risk assessment were fixed'}</p>
                    </TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  This validation process will analyze your cluster to determine if the security findings from this risk assessment have been remediated.
                  It performs comprehensive checks against the current cluster state to verify fix effectiveness.
                </p>
              </CardContent>
            </Card>

            {/* Validation Results */}
            {validationResults && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Validation Results
                    </span>
                    <Badge variant="outline" className="ml-2">
                      {validationResults.total_findings} findings validated
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                                        {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gradient-to-br from-neutral-50/50 to-neutral-100/50 dark:from-neutral-900/50 dark:to-neutral-800/50 rounded-xl border border-neutral-200/50 dark:border-neutral-700/50">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{validationResults.fixed_findings || 0}</div>
                        <div className="text-sm font-medium text-muted-foreground">Fixed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{validationResults.not_fixed_findings || 0}</div>
                        <div className="text-sm font-medium text-muted-foreground">Not Fixed</div>
                      </div>
                      {(validationResults.error_findings || 0) > 0 && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600 dark:text-orange-500">{validationResults.error_findings}</div>
                          <div className="text-sm font-medium text-muted-foreground">Validation Errors</div>
                        </div>
                      )}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-foreground">{validationResults.total_findings}</div>
                        <div className="text-sm font-medium text-muted-foreground">Total</div>
                      </div>
                    </div>

                    {/* Pagination Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-px h-8 bg-gradient-to-b from-green-500 to-blue-500"></div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Validation Details</h4>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {((validationCurrentPage - 1) * validationItemsPerPage) + 1}-{Math.min(validationCurrentPage * validationItemsPerPage, validationResults.results?.length || 0)} of {validationResults.results?.length || 0}
                      </div>
                    </div>

                    {/* Individual Results with Pagination */}
                    <div className="space-y-4">
                      {(validationResults.results || [])
                        .slice((validationCurrentPage - 1) * validationItemsPerPage, validationCurrentPage * validationItemsPerPage)
                        .map(result => (
                        <Card 
                          key={result.finding_id} 
                          className={cn(
                            "border-l-4 transition-all hover:shadow-md",
                            result.validation_status === 'fixed' ? 'border-l-green-500 bg-green-50/30 dark:bg-green-900/10' :
                            result.validation_status === 'not_fixed' ? 'border-l-red-500 bg-red-50/30 dark:bg-red-900/10' :
                            result.validation_status === 'error' ? 'border-l-orange-500 bg-orange-50/30 dark:bg-orange-900/10' :
                            'border-l-gray-500 bg-gray-50/30 dark:bg-gray-900/10'
                          )}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className={cn(
                                    "w-3 h-3 rounded-full flex-shrink-0",
                                    result.validation_status === 'fixed' ? 'bg-green-500' :
                                    result.validation_status === 'not_fixed' ? 'bg-red-500' :
                                    result.validation_status === 'error' ? 'bg-orange-500' :
                                    'bg-gray-500'
                                  )} />
                                  <Badge className={cn("text-xs font-medium",
                                    result.validation_status === 'fixed' ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' :
                                    result.validation_status === 'not_fixed' ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800' :
                                    result.validation_status === 'error' ? 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800' :
                                    'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800'
                                  )}>
                                    {result.validation_status === 'fixed' ? '✓ FIXED' :
                                     result.validation_status === 'not_fixed' ? '✗ NOT FIXED' :
                                     result.validation_status === 'error' ? '⚠ ERROR' :
                                     'UNKNOWN'}
                                  </Badge>
                                </div>
                                <h5 className="font-semibold text-foreground text-base mb-2">{result.finding_title}</h5>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="space-y-4">
                              {/* Summary */}
                              <div className="p-3 bg-muted/30 rounded-lg border">
                                <h6 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  Validation Summary
                                </h6>
                                <p className="text-sm text-muted-foreground leading-relaxed">{result.validation_summary}</p>
                              </div>
                              
                              {/* Validation Details */}
                              {result.validation_details.length > 0 && (
                                <div className="p-3 bg-muted/20 rounded-lg border">
                                  <h6 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                                    <Search className="h-4 w-4" />
                                    Validation Details
                                  </h6>
                                  <div className="space-y-2">
                                    {result.validation_details.map((detail, index) => (
                                      <div 
                                        key={index} 
                                        className="flex items-start gap-3 p-2 bg-background/50 rounded border-l-2 border-l-muted"
                                      >
                                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 mt-2 flex-shrink-0" />
                                        <p className="text-sm text-foreground leading-relaxed">{detail}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Validation Results Pagination */}
                    {(validationResults.results?.length || 0) > validationItemsPerPage && (
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Page {validationCurrentPage} of {Math.ceil((validationResults.results?.length || 0) / validationItemsPerPage)}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setValidationCurrentPage(Math.max(1, validationCurrentPage - 1))}
                            disabled={validationCurrentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </Button>
                          
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: Math.ceil((validationResults.results?.length || 0) / validationItemsPerPage) }, (_, i) => i + 1)
                              .filter(page => {
                                const totalPages = Math.ceil((validationResults.results?.length || 0) / validationItemsPerPage);
                                if (totalPages <= 7) return true;
                                if (page === 1 || page === totalPages) return true;
                                if (Math.abs(page - validationCurrentPage) <= 1) return true;
                                return false;
                              })
                              .map((page, index, array) => {
                                const prevPage = array[index - 1];
                                const showEllipsis = prevPage && page - prevPage > 1;
                                return (
                                  <React.Fragment key={page}>
                                    {showEllipsis && (
                                      <span className="text-gray-400 px-2">...</span>
                                    )}
                                    <Button
                                      variant={validationCurrentPage === page ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => setValidationCurrentPage(page)}
                                      className="w-8 h-8 p-0"
                                    >
                                      {page}
                                    </Button>
                                  </React.Fragment>
                                );
                              })}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setValidationCurrentPage(Math.min(Math.ceil((validationResults.results?.length || 0) / validationItemsPerPage), validationCurrentPage + 1))}
                            disabled={validationCurrentPage === Math.ceil((validationResults.results?.length || 0) / validationItemsPerPage)}
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Results State */}
            {!validationResults && !isValidatingFixes && (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Validation Results</h3>
                  <p className="text-muted-foreground mb-4">
                    Click "Validate Fixes" to check if the security findings have been remediated.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="thought" className="mt-6">
          <div className="space-y-6">
            {/* Agent Thinking Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
              {AGENT_TYPES_FOR_GRID.map((agentType) => {
                const agentState = agentThinking.get(agentType);
                const updates = agentState?.updates || [];
                const isExpanded = expandedAgents.has(agentType);
                const IconComponent = agentState?.icon || Shield;
                const name = agentState?.name || getAgentDisplayName(agentType);

                return (
                  <Card key={agentType} className="flex flex-col">
                    <button
                      onClick={() => toggleAgentExpansion(agentType)}
                      className={cn(
                        "group w-full flex flex-col gap-2 p-4 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 dark:from-blue-900/20 dark:to-cyan-900/20",
                        "hover:from-blue-100/90 hover:to-cyan-100/90 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/30",
                        "rounded-xl border border-blue-200/50 dark:border-blue-800/50 backdrop-blur-sm",
                        "transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl",
                        "focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 animate-pulse" style={{animationDuration: '2s'}}></div>
                            <IconComponent className="h-3 w-3 text-blue-600 dark:text-blue-400 relative z-10" />
                          </div>
                          <div className="font-medium text-blue-700 dark:text-blue-300 text-xs">{name}</div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAgentForModal(agentType);
                          }}
                          className="h-8 w-8 rounded-full"
                          disabled={updates.length === 0}
                        >
                          <Search className="h-4 w-4" />
                          <span className="sr-only">View details</span>
                        </Button>
                      </div>
                      
                      {isExpanded && (
                          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 max-h-48 overflow-y-auto">
                            {updates.length > 0 ? (
                              updates.map((update) => (
                                <div key={update.turn_number} className="mb-3">
                                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                                    <span>Turn {update.turn_number}</span>
                                    <span>{new Date(update.timestamp).toLocaleTimeString()}</span>
                                  </div>
                                  <MarkdownRenderer 
                                    content={update.thinking_overview} 
                                    className="text-xs leading-relaxed text-gray-700 dark:text-gray-300"
                                  />
                                                                {update.findings && (
                                  <div className="mt-3 p-2 bg-green-50/50 dark:bg-green-900/20 rounded-md border-l-2 border-green-500">
                                    <span className="text-xs font-medium text-green-700 dark:text-green-300">Agent Findings:</span>
                                    <div className="text-xs text-green-600 dark:text-green-400 mt-1 max-h-24 overflow-y-auto">
                                      <MarkdownRenderer 
                                        content={update.findings} 
                                        className="text-xs"
                                      />
                                    </div>
                                  </div>
                                )}
                                  {update.tool_calls && update.tool_calls.length > 0 && (
                                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                      <span>Tool Calls:</span>
                                      {update.tool_calls.map((toolCall, toolCallIndex) => (
                                        <div key={toolCallIndex} className="text-xs text-gray-700 dark:text-gray-300">
                                          <span>{toolCall.tool}:</span> {toolCall.cmd}
                                          {toolCall.success ? (
                                            <span className="text-green-500 text-xs"> (Success)</span>
                                          ) : (
                                            <span className="text-red-500 text-xs"> (Error: {toolCall.error})</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="text-center italic text-slate-500 dark:text-slate-400 py-4">
                                No updates from this agent yet.
                              </div>
                            )}
                          </div>
                      )}
                    </button>
                  </Card>
                );
              })}
            </div>

            {/* Orchestrator Chain of Thought */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-px h-8 bg-gradient-to-b from-purple-500 to-pink-500"></div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Orchestrator Chain of Thought</h3>
              </div>
              
              <Card>
                <CardContent className="p-6">
                  <div className="prose prose-sm dark:prose-invert max-w-none max-h-96 overflow-y-auto bg-gradient-to-br from-neutral-50/90 to-neutral-100/90 dark:from-neutral-900/90 dark:to-neutral-800/90 border border-neutral-200/50 dark:border-neutral-700/50 rounded-xl p-4 backdrop-blur-sm shadow-inner">
                    {(() => {
                      const orchestratorState = agentThinking.get('orchestrator');
                      if (orchestratorState && orchestratorState.updates.length > 0) {
                        return orchestratorState.updates.map((update) => (
                          <div key={update.turn_number} className="mb-6 border-b border-slate-200/50 dark:border-slate-700/50 pb-4 last:border-b-0">
                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-3">
                              <span className="font-medium">Turn {update.turn_number}</span>
                              <span>{new Date(update.timestamp).toLocaleTimeString()}</span>
                            </div>
                            <MarkdownRenderer 
                              content={update.thinking_overview} 
                              className="text-sm"
                            />
                            {update.findings && (
                              <div className="mt-3 p-2 bg-blue-50/50 dark:bg-blue-900/20 rounded-md border-l-2 border-blue-500">
                                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Findings:</span>
                                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  <MarkdownRenderer 
                                    content={update.findings} 
                                    className="text-xs"
                                  />
                                </div>
                              </div>
                            )}
                            {update.tool_calls && update.tool_calls.length > 0 && (
                              <div className="mt-3">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Tool Calls:</span>
                                <div className="mt-1 space-y-1">
                                  {update.tool_calls.map((toolCall, toolCallIndex) => (
                                    <div key={toolCallIndex} className="text-xs bg-gray-50 dark:bg-gray-800/50 p-2 rounded border">
                                      <div className="font-mono text-gray-700 dark:text-gray-300">
                                        <span className="font-semibold">{toolCall.tool}:</span> {toolCall.cmd}
                                      </div>
                                      {toolCall.success ? (
                                        <span className="text-green-600 dark:text-green-400 text-xs font-medium">✓ Success</span>
                                      ) : (
                                        <span className="text-red-600 dark:text-red-400 text-xs font-medium">✗ Error: {toolCall.error}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ));
                      } else {
                        return (
                          <div className="text-center py-8 text-slate-500 dark:text-slate-400 italic">
                            {assessment?.status === 'ongoing' ? (
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Waiting for orchestrator agent...</span>
                              </div>
                            ) : (
                              'No orchestrator chain of thought available'
                            )}
                          </div>
                        );
                      }
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Attack Path Analysis Tab */}
        <TabsContent value="attack-chains" className="mt-6">
          <div className="space-y-6">
            {/* Attack Path Analysis Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-px h-8 bg-gradient-to-b from-red-500 to-orange-500"></div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Attack Path Analysis</h3>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateAttackChains}
                    disabled={isGeneratingAttackChains || isLoadingAttackChains || !assessment?.structured_findings?.length}
                  >
                    {isGeneratingAttackChains ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    {isGeneratingAttackChains ? 'Generating...' : attackChains.length > 0 ? 'Regenerate Analysis' : 'Generate Analysis'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{attackChains.length > 0 ? 'Regenerate attack path analysis based on current findings' : 'Generate attack path analysis from risk assessment findings'}</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Staleness Warning - Show if any attack chain is marked as outdated */}
            {!isLoadingAttackChains && !isGeneratingAttackChains && attackChains.length > 0 && attackChains.some(chain => chain.is_outdated) && (
              <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-950/20 py-3">
                <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <AlertDescription className="text-sm text-orange-800 dark:text-orange-200">
                  <span className="font-semibold">This analysis is outdated.</span> Click "Regenerate Analysis" to update based on current findings.
                </AlertDescription>
              </Alert>
            )}

            {/* Loading State */}
            {isLoadingAttackChains && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <p>Loading attack path analysis...</p>
                </CardContent>
              </Card>
            )}

            {/* Generating State */}
            {isGeneratingAttackChains && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">Generating Attack Path Analysis...</p>
                  <p className="text-sm text-muted-foreground">
                    Analyzing findings and identifying potential attack paths. This may take up to 60 seconds.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Visualization */}
            {!isLoadingAttackChains && !isGeneratingAttackChains && (
              <AttackChainVisualization
                attackChains={attackChains}
                onStepClick={(chainId, stepNumber, findingId) => {
                  // Navigate to finding detail if findingId exists
                  if (findingId && assessmentId) {
                    navigate(`/risk-assessment/${assessmentId}/findings/${findingId}`);
                  }
                }}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {selectedAgentForModal && (
        <Dialog open={!!selectedAgentForModal} onOpenChange={(isOpen) => !isOpen && setSelectedAgentForModal(null)}>
          <DialogContent className="max-w-4xl h-4/5">
            <DialogHeader>
              <DialogTitle>Chain of Thought: {agentThinking.get(selectedAgentForModal)?.name}</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto p-4">
              {(agentThinking.get(selectedAgentForModal)?.updates || []).map((update, index) => (
                <div key={index} className="mb-4">
                  <p><strong>Turn {update.turn_number}:</strong> {update.thinking_overview}</p>
                  {update.tool_calls && update.tool_calls.length > 0 && (
                    <div className="mt-2">
                      <strong>Tool Calls:</strong>
                      {update.tool_calls.map((toolCall, toolCallIndex) => (
                        <div key={toolCallIndex} className="text-xs">
                          <p><strong>Tool:</strong> {toolCall.tool}</p>
                          <p><strong>Command:</strong> {toolCall.cmd}</p>
                          <p><strong>Output:</strong> <pre>{toolCall.output}</pre></p>
                          <p><strong>Success:</strong> {toolCall.success ? 'Yes' : 'No'}</p>
                          {toolCall.error && <p><strong>Error:</strong> {toolCall.error}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
    </TooltipProvider>
  );
};

export default KubernetesRiskAssessmentDetail;